/**
 * Saison-Knöpfe auf der Quellseite — pure Logik zur Einordnung aus dem Backend.
 *
 * Matze, 23.10.2026: „dass immer die relevanten Listen entweder für 26 oder für 27 über
 * einen Button wählbar sind." Ein Knopf je Saison, die unter den Katalogen vorkommt;
 * der Klick wählt die Katalogdatei(en) dieser Saison. Begleitdateien (DTB, Clubdeal)
 * sind nie wählbar — das Tool findet sie selbst, und angehakt fielen sie aus der Suche.
 */
import type { DateiEinordnung } from './typen';

/**
 * Welche Dateien der Saison-Knopf wählt. Pure.
 *
 * Nur Kataloge dieser Saison. Führt das Profil mehrere Dateien zusammen (JAKO), alle;
 * sonst die zuletzt geänderte — bei adidas ist das je Saison ohnehin genau eine PRICAT.
 * Dateien ohne erkennbare Saison bleiben außen vor; die wählt der Anwender von Hand.
 */
export function dateienDerSaison(
  einordnung: DateiEinordnung[],
  saison: string,
  mehrereDateien: boolean,
  geaendertAm: (id: string) => string | null,
): string[] {
  const kataloge = einordnung.filter(d => d.art === 'katalog' && d.saison === saison);
  if (kataloge.length === 0) return [];
  if (mehrereDateien) return kataloge.map(d => d.id);
  const zeit = (id: string) => { const t = Date.parse(geaendertAm(id) ?? ''); return Number.isNaN(t) ? -Infinity : t; };
  return [[...kataloge].sort((a, b) => zeit(b.id) - zeit(a.id))[0].id];
}

/**
 * Welche Saison beim Laden vorgewählt wird: die neueste (erster Eintrag — das Backend
 * sortiert neueste zuerst), sonst keine. Pure.
 */
export function saisonVorwahl(saisons: string[]): string | null {
  return saisons[0] ?? null;
}

/**
 * Zu welcher Saison die aktuelle Auswahl gehört — damit der Knopf der Auswahl folgt,
 * wenn der Anwender von Hand umhakt. Pure. null = keiner Saison eindeutig zuzuordnen.
 */
export function saisonDerAuswahl(einordnung: DateiEinordnung[], gewaehlt: string[]): string | null {
  const saisons = new Set(
    gewaehlt.map(id => einordnung.find(d => d.id === id)).map(d => (d?.art === 'katalog' ? d.saison : null)),
  );
  if (saisons.size !== 1) return null;
  return Array.from(saisons)[0] ?? null;
}

/** Ist die Datei anhakbar? Begleitdateien nie — das Tool findet sie selbst. Pure. */
export function istWaehlbar(datei: DateiEinordnung | undefined): boolean {
  return datei?.art !== 'begleitdatei';
}
